import type { Fragment } from '../translations';

// GitHub: the person's linked account and the admin's GitHub App.
export const github: Fragment = {
  fr: {
    'gh.link': 'Lier mon compte GitHub',
    'gh.unlink': 'Délier',
    'gh.recheck': 'Vérifier les droits',
    'gh.linkedAs': 'GitHub : @{login}',
    'gh.perm.admin': 'administration',
    'gh.perm.write': 'écriture',
    'gh.perm.read': 'lecture seule',
    'gh.perm.none': 'aucun accès',
    'gh.status.expired': 'lien expiré',
    'gh.hint.notLinked': 'Liez votre compte GitHub pour modifier les processus depuis l\'application : chaque modification est enregistrée à votre nom dans le dépôt.',
    'gh.hint.noWrite': 'Votre compte GitHub @{login} n\'a pas le droit d\'écrire dans {repo}. Demandez un accès en écriture au dépôt, puis vérifiez à nouveau.',
    'gh.hint.expired': 'Le lien avec votre compte GitHub @{login} a expiré. Reliez-le pour continuer.',
    'gh.block.notLinked': 'Pour modifier ce diagramme, liez d\'abord votre compte GitHub : la modification sera enregistrée à votre nom dans le dépôt.',
    'gh.block.expired': 'Le lien avec votre compte GitHub (@{login}) a expiré — reliez-le, puis réessayez.',
    'gh.block.noWrite': 'Votre compte GitHub @{login} n\'a pas le droit d\'écrire dans {repo}. Demandez l\'accès en écriture, puis cliquez sur « Vérifier les droits ».',
    'gh.block.newTab': 'Le lien s\'ouvre dans un nouvel onglet ; vos modifications restent ici.',
    'gh.error.linkFailed': 'La liaison avec GitHub a échoué ({reason}). Réessayez.',
    'gh.app.title': 'Application GitHub',
    'gh.app.intro': 'L\'application donne à CdE sa propre identité sur GitHub : elle lit {repo} pour tout le monde, et chaque personne modifie les processus avec son propre compte GitHub — les droits du dépôt restent la règle.',
    'gh.app.notConfigured': 'Aucune application GitHub n\'est encore créée pour cette instance.',
    'gh.app.create': 'Créer l\'application GitHub',
    'gh.app.createHint': 'Vous serez envoyé·e sur github.com, connecté·e avec le compte propriétaire du dépôt ({owner}). Confirmez la création, puis choisissez le dépôt {repo} à l\'installation.',
    'gh.app.notInstalled': 'pas encore installée sur {repo}',
    'gh.app.install': 'Installer sur {repo}',
    'gh.app.installed': 'installée sur {repo}',
    'gh.app.norepo': 'L\'application est installée, mais pas sur {repo}. Modifiez l\'installation sur GitHub pour y ajouter le dépôt.',
    'gh.app.refresh': 'Revérifier l\'installation',
    'gh.app.forget': 'Oublier l\'application',
    'gh.app.forgetConfirm': 'Oublier l\'application GitHub ? Les comptes liés seront déliés ; l\'application elle-même reste à supprimer sur GitHub.',
    'gh.app.source.env': 'configurée par variables d\'environnement',
    'gh.app.slug': 'Application',
    'gh.app.state': 'État',
    'gh.app.installedAt': 'Installée le',
    'gh.app.repos': 'Dépôts couverts',
    'gh.app.mode': 'Lecture par l\'application, écriture au nom de chaque personne avec son compte GitHub.',
    'gh.app.links': 'Comptes GitHub liés',
    'gh.app.noLinks': 'Personne n\'a encore lié son compte GitHub.',
    'gh.app.col.user': 'Utilisateur',
    'gh.app.col.github': 'Compte GitHub',
    'gh.app.col.permission': 'Droit sur le dépôt',
    'gh.app.col.since': 'Lié le',
    'gh.app.flash.installed': 'Application GitHub installée — les personnes peuvent maintenant lier leur compte.',
    'gh.app.flash.error': 'L\'étape GitHub a échoué ({reason}). Recommencez.',
    'gh.writeMode.server': 'Les modifications sont enregistrées avec un jeton partagé (mode hérité).',
  },
  en: {
    'gh.link': 'Link my GitHub account',
    'gh.unlink': 'Unlink',
    'gh.recheck': 'Check my rights',
    'gh.linkedAs': 'GitHub: @{login}',
    'gh.perm.admin': 'admin',
    'gh.perm.write': 'write',
    'gh.perm.read': 'read only',
    'gh.perm.none': 'no access',
    'gh.status.expired': 'link expired',
    'gh.hint.notLinked': 'Link your GitHub account to edit processes from the app: every change is committed to the repository under your name.',
    'gh.hint.noWrite': 'Your GitHub account @{login} has no write access to {repo}. Ask for write access to the repository, then check again.',
    'gh.hint.expired': 'The link with your GitHub account @{login} has expired. Link it again to continue.',
    'gh.block.notLinked': 'To edit this diagram, link your GitHub account first: the change will be committed to the repository under your name.',
    'gh.block.expired': 'The link with your GitHub account (@{login}) has expired — link it again, then retry.',
    'gh.block.noWrite': 'Your GitHub account @{login} has no write access to {repo}. Ask for write access, then click "Check my rights".',
    'gh.block.newTab': 'The link opens in a new tab; your changes stay here.',
    'gh.error.linkFailed': 'Linking with GitHub failed ({reason}). Try again.',
    'gh.app.title': 'GitHub App',
    'gh.app.intro': 'The app gives CdE its own identity on GitHub: it reads {repo} for everyone, and each person edits processes with their own GitHub account — the repository\'s permissions remain the rule.',
    'gh.app.notConfigured': 'No GitHub App has been created for this instance yet.',
    'gh.app.create': 'Create the GitHub App',
    'gh.app.createHint': 'You will be sent to github.com, signed in as the repository owner ({owner}). Confirm the creation, then pick the {repo} repository when installing.',
    'gh.app.notInstalled': 'not yet installed on {repo}',
    'gh.app.install': 'Install on {repo}',
    'gh.app.installed': 'installed on {repo}',
    'gh.app.norepo': 'The app is installed, but not on {repo}. Edit the installation on GitHub to add the repository.',
    'gh.app.refresh': 'Re-check the installation',
    'gh.app.forget': 'Forget the app',
    'gh.app.forgetConfirm': 'Forget the GitHub App? Linked accounts will be unlinked; the app itself still has to be deleted on GitHub.',
    'gh.app.source.env': 'configured through environment variables',
    'gh.app.slug': 'App',
    'gh.app.state': 'State',
    'gh.app.installedAt': 'Installed on',
    'gh.app.repos': 'Repositories covered',
    'gh.app.mode': 'Reads by the app, writes in each person\'s name with their GitHub account.',
    'gh.app.links': 'Linked GitHub accounts',
    'gh.app.noLinks': 'Nobody has linked a GitHub account yet.',
    'gh.app.col.user': 'User',
    'gh.app.col.github': 'GitHub account',
    'gh.app.col.permission': 'Repository right',
    'gh.app.col.since': 'Linked on',
    'gh.app.flash.installed': 'GitHub App installed — people can now link their account.',
    'gh.app.flash.error': 'The GitHub step failed ({reason}). Start again.',
    'gh.writeMode.server': 'Changes are committed with a shared token (legacy mode).',
  },
};
